import { useRef } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import Router from 'next/router';

import InsightsLoadingSkeleton from 'Components/Insights/components/InsightsLoadingSkeleton/InsightsLoadingSkeleton';
import EmptyAndErrorView, {
	View,
} from 'Components/Insights/ResultsPage/components/EmptyAndErrorView/EmptyAndErrorView';
import {
	EmptyView,
	ErrorView,
	LoadingView,
	QueryViewStateHandler,
	SuccessView,
} from 'Components/QueryViewStateHandler/QueryViewStateHandler';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import {
	triggerInsightsResultsEmptyStateCTAClickedEvent,
	triggerInsightsResultsEmptyStateLoadedEvent,
	triggerInsightsResultsErrorStateCTAClickedEvent,
} from 'Services/analytics/events';
import type { GetSimilarVehiclesResponseWithVersion } from 'Services/api/vehicles.types';
import { sendBrazeInsightsSearchEvent } from 'Services/braze/brazeEvents';
import { useInsightsStore } from 'Stores/InsightsStore/InsightsStore';
import { useInsightsRecentSold } from 'Utilities/hooks/recentSoldPrices/useRecentSoldPrices';
import ROUTES from 'Utilities/routes';

import { DataDisclaimer } from '../DataDisclaimer/DataDisclaimer';
import { OverThePastYear } from '../OverThePastYear/OverThePastYear';
import { RecentSoldPricesDataGrid } from '../RecentSoldPricesDataGrid/RecentSoldPricesDataGrid';
import { RecentSoldPricesDataList } from '../RecentSoldPricesDataList/RecentSoldPricesDataList';
import { SimilarVehicles } from '../SimilarVehicles/SimilarVehicles';
import { Title } from '../Title/Title';

import styles from './RecentSoldPrices.module.scss';

export const RecentSoldPrices: React.FC = () => {
	const { make, model } = useInsightsStore((state) => state);

	const insightsRecentSold = useInsightsRecentSold({
		makeId: make.id,
		modelId: model.id,
	});

	const { data, isError, isLoading, refetch } = insightsRecentSold;
	const isEmpty = data && data.vehicles.length === 0;
	const isSuccess = !isError && !isLoading && !isEmpty;

	const {
		maxWidth: { breakpointTablet: isTablet },
	} = useBreakpoint();

	const onEmptyStateCTAClick = () => {
		triggerInsightsResultsEmptyStateCTAClickedEvent();
		void Router.push(ROUTES.INSIGHTS_LANDING.href);
	};

	const onErrorCTAClick = () => {
		triggerInsightsResultsErrorStateCTAClickedEvent();
		void refetch();
	};

	const similarVehiclesData = useRef<GetSimilarVehiclesResponseWithVersion | undefined | null>(null);

	const handleSimilarVehiclesQueryUpdate = (query: UseQueryResult<GetSimilarVehiclesResponseWithVersion>) => {
		if (!query.isLoading && query.data !== similarVehiclesData.current) {
			similarVehiclesData.current = query.data;
			void sendBrazeInsightsSearchEvent({
				makePretty: make.name,
				modelPretty: model.namePretty,
				similarVehiclesEnquiryIds: query.data?.similarVehicles.map((vehicle) => vehicle.id) ?? [],
			});
		}
	};

	return (
		<section aria-busy={isLoading} aria-live="polite" className={styles.container}>
			<Title insightsRecentSold={insightsRecentSold} />
			<QueryViewStateHandler isEmpty={isEmpty} isError={isError} isLoading={isLoading} isSuccess={isSuccess}>
				<SuccessView>
					<OverThePastYear insightsRecentSold={insightsRecentSold} />
					{isTablet ? (
						<RecentSoldPricesDataList
							insightsRecentSold={insightsRecentSold}
							prettyMake={make.name}
							prettyModel={model.namePretty}
						/>
					) : (
						<RecentSoldPricesDataGrid
							insightsRecentSold={insightsRecentSold}
							prettyMake={make.name}
							prettyModel={model.namePretty}
						/>
					)}
					{!isError && (data?.vehicles.length ?? 0) > 0 && (
						<SimilarVehicles make={make.name} modelId={model.id} onQueryUpdate={handleSimilarVehiclesQueryUpdate} />
					)}
					{isTablet && <DataDisclaimer />}
				</SuccessView>
				<EmptyView handleOnEmpty={() => triggerInsightsResultsEmptyStateLoadedEvent()}>
					<EmptyAndErrorView handleOnClick={onEmptyStateCTAClick} view={View.EMPTY} />
				</EmptyView>
				<LoadingView>
					<InsightsLoadingSkeleton />
				</LoadingView>
				<ErrorView>
					<EmptyAndErrorView handleOnClick={onErrorCTAClick} view={View.ERROR} />
				</ErrorView>
			</QueryViewStateHandler>
		</section>
	);
};
